import { useCallback, useEffect, useRef } from 'react'

import axios from '../../dataClient/dataClient'

export const DEFAULT_WORKFLOW_POLL_INTERVAL_MS = 1000
const DEFAULT_WORKFLOW_POLL_TIMEOUT_MS = 10 * 60 * 1000

export class WorkflowPollTimeoutError extends Error {
  constructor(workflowId, timeoutMs) {
    super(`Workflow ${workflowId} was still pending after ${Math.round(timeoutMs / 1000)} s`)
    this.name = 'WorkflowPollTimeoutError'
    this.workflowId = workflowId
    this.timeoutMs = timeoutMs
  }
}

export class WorkflowPollCancelledError extends Error {
  constructor(workflowId) {
    super(`Polling of workflow ${workflowId} was cancelled`)
    this.name = 'WorkflowPollCancelledError'
    this.workflowId = workflowId
  }
}

const fetchWorkflow = async workflowId => (await axios.get(`/workflow/${workflowId}`)).data.data

const isTerminal = data => Boolean(data) && data.status !== 'PENDING'
const pageIsVisible = () => typeof document === 'undefined' || !document.hidden

function sleep(ms, signal) {
  return new Promise(resolve => {
    if (signal?.aborted) {
      resolve()
      return
    }
    const onAbort = () => {
      clearTimeout(timer)
      resolve()
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

// Poll until terminal, pausing while hidden and respecting cancellation and timeout.
export async function pollUntilTerminal(
  workflowId,
  {
    fetch = fetchWorkflow,
    intervalMs = DEFAULT_WORKFLOW_POLL_INTERVAL_MS,
    timeoutMs = DEFAULT_WORKFLOW_POLL_TIMEOUT_MS,
    signal,
    isVisible = pageIsVisible
  } = {}
) {
  const startedAt = Date.now()
  for (;;) {
    if (signal?.aborted) throw new WorkflowPollCancelledError(workflowId)
    if (isVisible()) {
      const data = await fetch(workflowId)
      if (signal?.aborted) throw new WorkflowPollCancelledError(workflowId)
      if (isTerminal(data)) return data
    }
    if (Date.now() - startedAt >= timeoutMs) throw new WorkflowPollTimeoutError(workflowId, timeoutMs)
    await sleep(intervalMs, signal)
  }
}

// Provides a workflow poller that cancels all active requests on unmount.
export function useWorkflowPoll({ intervalMs = DEFAULT_WORKFLOW_POLL_INTERVAL_MS, timeoutMs = DEFAULT_WORKFLOW_POLL_TIMEOUT_MS } = {}) {
  const controllerRef = useRef(null)
  if (controllerRef.current === null) controllerRef.current = new AbortController()

  useEffect(() => {
    // Ensure no poll outlives the component.
    if (controllerRef.current.signal.aborted) controllerRef.current = new AbortController()
    return () => controllerRef.current.abort()
  }, [])

  const poll = useCallback(
    (workflowId, options = {}) => pollUntilTerminal(workflowId, { intervalMs, timeoutMs, signal: controllerRef.current.signal, ...options }),
    [intervalMs, timeoutMs]
  )

  const cancel = useCallback(() => {
    controllerRef.current.abort()
    controllerRef.current = new AbortController()
  }, [])

  return { poll, cancel }
}

export default useWorkflowPoll
